import React, {useId, useRef, useState} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import PropTypes from 'prop-types'
import {Box, Flex, useBreakpointValue} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import {useIntl} from 'react-intl'
import Carousel from '../../../components/carousel/index.jsx'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {ArrowButton} from '../../../components/arrow-button/index.jsx'
import {isHydrated} from '../../../utils/utils.js'

const desktopCarouselHeight = 900

/**
 * PDCCarouselImageGallery is a responsive image gallery component that displays a collection of image tiles in either a carousel or a static layout, depending on the number of tiles and the current screen size.
 *
 * The component dynamically determines the number of slides to show at various breakpoints (small, medium, large, xlarge, xxlarge) and adapts the layout accordingly. If the number of tiles exceeds the number of slides to show, a carousel with navigation arrows is rendered; otherwise, the tiles are displayed in a centered flex list.
 *
 * Tile dimensions are calculated responsively to maintain image aspect ratios, with special handling for mobile and desktop environments. The component leverages internationalization for accessibility labels and supports display priority for tiles.
 *
 * Props:
 * @param {number} smallNbSlidesToShow - Number of slides to show on small screens.
 * @param {number} mediumNbSlidesToShow - Number of slides to show on medium screens.
 * @param {number} largeNbSlidesToShow - Number of slides to show on large screens.
 * @param {number} xlargeNbSlidesToShow - Number of slides to show on extra-large screens.
 * @param {number} xxlargeNbSlidesToShow - Number of slides to show on double extra-large screens.
 * @param {number} displayPriority - Priority value to control the display order or prominence of tiles.
 * @param {Array} regions - Array of region objects, each containing a list of tile components to be displayed in the gallery.
 *
 * The component uses several hooks for responsive behavior, internationalization, and carousel state management. It also provides accessibility features such as ARIA labels and keyboard navigation support.
 */
export const PDCCarouselImageGallery = ({
    smallNbSlidesToShow,
    mediumNbSlidesToShow,
    largeNbSlidesToShow,
    xlargeNbSlidesToShow,
    xxlargeNbSlidesToShow,
    displayPriority,
    regions
}) => {
    const intl = useIntl()
    const [swiper, setSwiper] = useState()
    const firstTileRef = useRef(null)

    /**
     * Calculates and returns the dimensions for a mobile tile based on the current window width and the tile's image metadata.
     *
     * If the environment is hydrated (i.e., running in the browser), the tile width is set to 90% of the window's inner width,
     * and the height is calculated to maintain the aspect ratio of the tile's small image using its metadata.
     * If not hydrated, returns 'auto' for both width and height.
     *
     * @param {Object} tile - The tile object containing image data and metadata.
     * @param {Object} tile.data - The data object associated with the tile.
     * @param {Object} tile.data.smallImage - The small image object for the tile.
     * @param {Object} tile.data.smallImage.metaData - Metadata for the small image, including width and height.
     * @param {number} tile.data.smallImage.metaData.width - The width of the small image.
     * @param {number} tile.data.smallImage.metaData.height - The height of the small image.
     * @returns {Object} An object containing the calculated width and height for the tile.
     * @returns {number|string} return.width - The calculated width in pixels or 'auto' if not hydrated.
     * @returns {number|string} return.height - The calculated height in pixels or 'auto' if not hydrated.
     */
    const getMobileTileDimensions = (tile) => {
        if (isHydrated()) {
            const tileWidth = window?.innerWidth * 0.9
            const metadata = tile?.data?.smallImage?.metaData
            const tileHeight = (metadata?.height * tileWidth) / metadata?.width

            return {width: tileWidth, height: tileHeight}
        }
        return {width: 'auto', height: 'auto'}
    }

    /**
     * Calculates the proportional width of an image tile based on a given height, preserving the original aspect ratio.
     *
     * Retrieves the original width and height from the tile's small image metadata and computes the new width
     * that corresponds to the specified height. If the necessary metadata is missing, the function may return NaN.
     *
     * @param {number} height - The desired height for the image tile.
     * @param {Object} tile - The tile object containing image metadata.
     * @param {Object} tile.data - The data object of the tile.
     * @param {Object} tile.data.smallImage - The small image object within the tile data.
     * @param {Object} tile.data.smallImage.metaData - The metadata object containing width and height.
     * @param {number} tile.data.smallImage.metaData.width - The original width of the image.
     * @param {number} tile.data.smallImage.metaData.height - The original height of the image.
     * @returns {number} The calculated width that maintains the image's aspect ratio for the given height.
     */
    const getWidthFromHeight = (height, tile) => {
        const metadata = tile?.data?.smallImage?.metaData
        return (metadata?.width * height) / metadata?.height
    }

    // Get the tiles from the regions
    let tiles = []
    let firstTileDimensions = {width: 'auto', height: 'auto'}
    if (regions) {
        tiles = regions[0].components || []
        firstTileDimensions = getMobileTileDimensions(tiles[0])
    }

    // Get the number of slides to show depending on the screen size
    const nbSlidesToShowByBreakpoint = usePDCResponsiveElement({
        smallEl: smallNbSlidesToShow || 1,
        mediumEl: mediumNbSlidesToShow || 2,
        largeEl: largeNbSlidesToShow,
        xlargeEl: xlargeNbSlidesToShow,
        xxlargeEl: xxlargeNbSlidesToShow
    })
    const currentNbSlidesToShow = useBreakpointValue(nbSlidesToShowByBreakpoint)

    const titleId = useId()

    const carouselSettings = {
        dots: false,
        arrows: true,
        infinite: true,
        speed: 500,
        variableWidth: true,
        slidesToShow: 1,
        prevArrow: <ArrowButton direction="prev" onClick={() => swiper?.slickPrev()} />,
        nextArrow: <ArrowButton direction="next" onClick={() => swiper?.slickNext()} />
    }

    return (
        <Box as="section" aria-describedby={titleId} css={[PDCCarouselImageGallery.css]}>
            {/* Display a carousel only if there are more tiles than the number of slides to show */}
            {tiles && tiles.length > currentNbSlidesToShow ? (
                <Carousel
                    settings={carouselSettings}
                    label={intl.formatMessage({
                        id: 'carousel.image.gallery.label',
                        defaultMessage: 'Carousel Image Gallery'
                    })}
                    setSwiper={setSwiper}
                    nbSlidesToShowByBreakpoint={nbSlidesToShowByBreakpoint}
                >
                    {tiles.map((tile, index) => {
                        tile.displayPriority = displayPriority
                        return (
                            <Box key={tile?.id || index}>
                                <Box
                                    width={[
                                        index === 0
                                            ? firstTileDimensions.width
                                            : getWidthFromHeight(firstTileDimensions.height, tile),
                                        null,
                                        getWidthFromHeight(desktopCarouselHeight, tile)
                                    ]}
                                    height={[
                                        firstTileDimensions.height,
                                        null,
                                        `${desktopCarouselHeight}px`
                                    ]}
                                    ref={index === 0 ? firstTileRef : null}
                                    className={'slide-container'}
                                >
                                    <Component component={tile} />
                                </Box>
                            </Box>
                        )
                    })}
                </Carousel>
            ) : (
                <Flex as="ul" listStyleType="none" display="flex" justifyContent="center">
                    {tiles.map((tile, index) => {
                        tile.nbSlides = currentNbSlidesToShow
                        tile.displayPriority = displayPriority
                        return (
                            <li key={tile?.id || index}>
                                <Component component={tile} />
                            </li>
                        )
                    })}
                </Flex>
            )}
        </Box>
    )
}

PDCCarouselImageGallery.css = css`
    .slick-slider {
    }
    .slick-list {
        overflow: visible;
    }
    .slick-track {
        display: flex;
    }
    .slick-slide {
        flex: none;

        & > div {
            display: flex;
        }
    }
    .slide-container {
        div {
            height: 100%;
        }
    }
    .slick-arrow {
        opacity: 0;
        pointer-events: none;
        position: absolute;
        left: auto;
        right: 86px;
        bottom: 40px;
        top: auto;
        width: 40px;
        height: 40px;
        background: white;
        z-index: 1;
        transform: none;
        color: var(--chakra-colors-fullBlack);
        border: 1px solid var(--chakra-colors-gray);
        &::before {
            content: '';
        }
        &:focus {
            opacity: 1;
        }

        @media screen and (min-width: 768px) {
            opacity: 1;
            pointer-events: auto;
        }
    }
    .slick-next {
        right: 40px;
    }
`

PDCCarouselImageGallery.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    smallNbSlidesToShow: PropTypes.number,
    mediumNbSlidesToShow: PropTypes.number,
    largeNbSlidesToShow: PropTypes.number,
    xlargeNbSlidesToShow: PropTypes.number,
    xxlargeNbSlidesToShow: PropTypes.number,
    displayPriority: PropTypes.bool,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
